import select from 'dom-select';
import { mqFinePointer } from '../../../js/lib/breakpoints';

const OPEN_DELAY = 150;
const CLOSE_DELAY = 300;

// Hover-to-open for main nav items that pair a landing page link with a subnav toggle.
// Clicks, Escape and click-outside stay in nav-item.init.js; aria-expanded on the
// toggle is the single source of truth, with data-opened-by="hover|click".
// Items without a link (a toggle only) stay click-only.
export const GlobalNav = (el) => {
  const finePointer = window.matchMedia(mqFinePointer);

  const items = Array.from(select.all('.global-nav__main > li', el))
    .map((li) => ({
      li,
      link: li.querySelector(':scope > .nav-item--link'),
      toggle: li.querySelector(':scope > .nav-item--toggle'),
    }))
    .filter(({ link, toggle }) => link && toggle);

  let openTimer;
  let closeTimer;

  const isOpen = (item) => item.toggle.getAttribute('aria-expanded') === 'true';

  const close = (item) => {
    item.toggle.setAttribute('aria-expanded', 'false');
    delete item.toggle.dataset.openedBy;
  };

  const open = (item) => {
    items.forEach((other) => {
      if (other !== item && isOpen(other)) close(other);
    });
    item.toggle.setAttribute('aria-expanded', 'true');
    item.toggle.dataset.openedBy = 'hover';
  };

  const handlePointerEnter = (item, event) => {
    if (event.pointerType !== 'mouse' || !finePointer.matches) return;

    clearTimeout(openTimer);
    clearTimeout(closeTimer);

    if (isOpen(item) || item.toggle.dataset.hoverSuppressed) return;

    // Moving across the bar with a subnav already open switches right away
    if (items.some(isOpen)) {
      open(item);
      return;
    }

    openTimer = setTimeout(() => open(item), OPEN_DELAY);
  };

  const handlePointerLeave = (item, event) => {
    if (event.pointerType !== 'mouse') return;

    clearTimeout(openTimer);
    delete item.toggle.dataset.hoverSuppressed;

    // The subnav is a child of the li, so moving into it doesn't count as leaving
    if (item.toggle.dataset.openedBy !== 'hover') return;

    closeTimer = setTimeout(() => close(item), CLOSE_DELAY);
  };

  // Keyboard: close when focus moves on to another item or out of the nav.
  // A null relatedTarget (a click on the subnav's background) is left to click-outside.
  const handleFocusOut = (item, event) => {
    if (!finePointer.matches || !event.relatedTarget) return;
    if (item.li.contains(event.relatedTarget)) return;
    if (item.toggle.dataset.openedBy === 'hover') return;

    if (isOpen(item)) close(item);
  };

  // Leaving pointer mode (resize, docking a tablet) drops hover-opened subnavs
  const handleModeChange = () => {
    clearTimeout(openTimer);
    clearTimeout(closeTimer);
    items.forEach((item) => {
      if (item.toggle.dataset.openedBy === 'hover') close(item);
    });
  };

  const addEvents = () => {
    items.forEach((item) => {
      item.li.addEventListener('pointerenter', (event) => handlePointerEnter(item, event));
      item.li.addEventListener('pointerleave', (event) => handlePointerLeave(item, event));
      item.li.addEventListener('focusout', (event) => handleFocusOut(item, event));
    });

    finePointer.addEventListener('change', handleModeChange);
  };

  const init = () => {
    if (!items.length) return;

    addEvents();
  };

  init();
};

export default GlobalNav;
